import { z } from "zod"

// Sanity Image
export const sanityReferenceSchema = z.object({
  _ref: z.string(),
})

export const sanityAssetSchema = z.record(z.any()).and(
  z.object({
    _id: z.string().optional(),
    url: z.string().optional(),
    path: z.string().optional(),
    assetId: z.string().optional(),
    extension: z.string().optional(),
  }),
)

export const sanityImageCropSchema = z.object({
  _type: z.string().optional(),
  left: z.number(),
  bottom: z.number(),
  right: z.number(),
  top: z.number(),
})

export const sanityImageHotspotSchema = z.object({
  _type: z.string().optional(),
  width: z.number(),
  height: z.number(),
  x: z.number(),
  y: z.number(),
})

export const sanityImageObjectSchema = z.object({
  _key: z.string().optional(),
  asset: z.union([sanityReferenceSchema, sanityAssetSchema]),
  crop: sanityImageCropSchema.optional(),
  hotspot: sanityImageHotspotSchema.optional(),
})

export const sanityContentImageSchema = z
  .object({
    _type: z.literal("content_image"),
    alt_text: z.string().optional(),
  })
  .and(sanityImageObjectSchema)

export const sanityThumbnailSchema = z
  .object({
    _type: z.literal("thumbnail"),
    alt_text: z.string().optional(),
  })
  .and(sanityImageObjectSchema)

// Portable Text Block
export const portableTextBlockStyleSchema = z.union([
  z.literal("normal"),
  z.literal("blockquote"),
  z.literal("h1"),
  z.literal("h2"),
  z.literal("h3"),
  z.literal("h4"),
  z.literal("h5"),
  z.literal("h6"),
  z.string(),
])

export const portableTextLinkSchema = z.object({
  _type: z.literal("link"),
  _key: z.string().optional(),
  href: z.string(),
})

export const portableTextListItemTypeSchema = z.union([
  z.literal("bullet"),
  z.literal("number"),
])

export const portableTextSpanSchema = z.object({
  _type: z.literal("span"),
  _key: z.string().optional(),
  text: z.string(),
  marks: z.array(z.string()).optional(),
})

export const portableTextBlockSchema = z.object({
  _type: z.literal("block"),
  _key: z.string().optional(),
  children: z.array(
    z.object({
      _key: z.string(),
      _type: z.literal("span"),
      marks: z.array(
        z.union([
          z.literal("strong"),
          z.literal("em"),
          z.literal("strike-through"),
          z.string(),
        ]),
      ),
      text: z.string(),
    }),
  ),
  markDefs: z.array(portableTextLinkSchema),
  style: portableTextBlockStyleSchema,
  listItem: portableTextListItemTypeSchema.optional(),
  level: z.number().optional(),
})

// Sanity Video
export const sanityContentVideoSchema = z.object({
  _type: z.literal("content_video"),
  _key: z.string().optional(),
  video_url: z.string(),
  alt_text: z.string().optional(),
})

export type SanityAssetDto = z.infer<typeof sanityAssetSchema>

export type SanityThumbnailDto = z.infer<typeof sanityThumbnailSchema>

export type SanityContentImageDto = z.infer<typeof sanityContentImageSchema>

export type SanityPortableTextBlockDto = z.infer<typeof portableTextBlockSchema>

export type SanityContentVideoDto = z.infer<typeof sanityContentVideoSchema>
